import { selectSharedViewDocument } from "./sharedViewSelectors";
import { useEffect, useRef, useState } from "react";
import { Alert, Button, Group, Modal, Stack, Text, TextInput, Tooltip } from "@mantine/core";
import { FiCheck, FiCopy, FiLink } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../app/hooks";
import { selectSharedViewState } from "../viewSelectors";
import { setActiveSharedViewInRedux } from "../../ViewerRedux/viewerSlice";
import { useCreateSharedViewMutation } from "./sharedViewsApi";
import { sharedViewFingerprint, sharedViewPath } from "./sharedViewUrl";
import { flushViewerCameraUpdate } from "../Viewer/cameraUpdateQueue";

export function ShareViewButton() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [createView, { isLoading }] = useCreateSharedViewMutation();
  const [opened, setOpened] = useState(false);
  const [link, setLink] = useState("");
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);
  const busy = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const share = async () => {
    if (busy.current) return;
    busy.current = true;
    setOpened(true);
    setLink("");
    setError("");
    setCopied(false);
    setCopyError(false);
    try {
      flushViewerCameraUpdate(dispatch);
      const { projectNo, snapshot } = dispatch((_dispatch, getState) => ({
        projectNo: getState().design.projectNo,
        snapshot: selectSharedViewState(getState()),
      }));
      if (!projectNo || !snapshot) {
        setError("Visningen er ikke klar. Åpne en etasje og vent til visningen er lastet, og prøv igjen.");
        return;
      }
      const response = await createView({ body: { projectNo, data: { kind: "snapshot", ...snapshot } } }).unwrap();
      const saved = selectSharedViewDocument(response);
      if (!saved) throw new Error("Expected a snapshot response");
      if (!mounted.current) return;
      const path = sharedViewPath(projectNo, saved._id);
      setLink(new URL(path, window.location.origin).href);
      // Do not rewind the user's view if it changed while the save was in flight.
      flushViewerCameraUpdate(dispatch);
      const latest = dispatch((_dispatch, getState) => ({
        projectNo: getState().design.projectNo,
        snapshot: selectSharedViewState(getState()),
      }));
      if (latest.projectNo === projectNo && latest.snapshot &&
        sharedViewFingerprint(latest.snapshot) === sharedViewFingerprint(snapshot)) {
        dispatch(setActiveSharedViewInRedux({ id: saved._id, state: { view: saved.view, context: saved.context } }));
        navigate(path, { replace: true });
      }
    } catch {
      if (mounted.current) setError("Kunne ikke opprette lenken. Prøv igjen.");
    } finally {
      busy.current = false;
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setCopyError(false);
    } catch {
      setCopyError(true);
    }
  };

  return <>
    <Tooltip label="Del etasje, kamera, valgt rom og komponentfilter">
      <span>
        <Button size="sm" leftSection={<FiLink size={16} />} loading={isLoading} onClick={share}>
          Del visning
        </Button>
      </span>
    </Tooltip>
    <Modal opened={opened} onClose={() => setOpened(false)} title={link ? "Visningen er klar" : "Del visning"} centered size="md" closeOnClickOutside={!isLoading} closeOnEscape={!isLoading} withCloseButton={!isLoading}>
      <Stack gap="md">
        {isLoading && <Text size="sm" role="status">Oppretter lenke til visningen…</Text>}
        {error && <Alert color="red" role="alert">{error}</Alert>}
        {link && <>
          <Text size="sm">Kopier lenken og send den til kollegene dine.</Text>
          <TextInput data-autofocus label="Lenke til visningen" value={link} readOnly onFocus={event => event.currentTarget.select()} />
          <Text size="xs" c="dimmed">Lenken inneholder etasje, kamera, valgt rom og komponentfilter. Mottakeren må ha tilgang til prosjektet.</Text>
          {copyError && <Text size="sm" c="red" role="alert">Kunne ikke kopiere automatisk. Marker lenken og kopier den manuelt.</Text>}
          {copied && <Text size="sm" c="teal" role="status">Lenken er kopiert.</Text>}
        </>}
        <Group justify="flex-end">
          <Button variant="neutral" disabled={isLoading} onClick={() => setOpened(false)}>Lukk</Button>
          {error && <Button onClick={share}>Prøv igjen</Button>}
          {link && <Button leftSection={copied ? <FiCheck /> : <FiCopy />} onClick={copy}>{copied ? "Kopiert" : "Kopier lenke"}</Button>}
        </Group>
      </Stack>
    </Modal>
  </>;
}
